import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const AUTOPLAY_MS = 6000;
const TRANSITION_S = 0.8;

const GalleryFrame = ({ images, onOpen, testid = "gallery-frame" }) => {
  const { t } = useLang();
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const [touchX, setTouchX] = useState(null);

  const nav = useCallback(
    (d) => setIndex((i) => (i + d + images.length) % images.length),
    [images.length]
  );

  useEffect(() => {
    const onVis = () => setTabHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    if (paused || tabHidden || reduced || images.length < 2) return;
    const id = window.setInterval(() => nav(1), AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [index, paused, tabHidden, reduced, images.length, nav]);

  const onTouchEnd = (e) => {
    if (touchX === null) return;
    const dx = e.changedTouches[0].clientX - touchX;
    if (Math.abs(dx) > 40) nav(dx < 0 ? 1 : -1);
    setTouchX(null);
  };

  const img = images[index];
  const next = images[(index + 1) % images.length];
  const offset =
    reduced || images.length < 2 ? "" : index % 2 === 0 ? "lg:translate-x-[5%]" : "lg:-translate-x-[5%]";
  const dur = reduced ? 0.01 : TRANSITION_S;

  return (
    <div data-testid={testid} className="select-none">
      <div
        className={`relative mx-auto h-[340px] w-full transition-transform duration-700 sm:h-[460px] lg:h-[600px] ${offset}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
        onTouchEnd={onTouchEnd}
      >
        <AnimatePresence mode="sync">
          <motion.button
            key={img.src}
            type="button"
            onClick={() => onOpen?.(index)}
            aria-label={`${t.lightbox.view} ${img.caption}`}
            data-testid={`${testid}-image`}
            className="absolute inset-0 flex cursor-pointer items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: dur, ease: "easeInOut" }}
          >
            <span
              role="group"
              aria-label={`${t.gallery.frameAria}: ${img.caption}`}
              className="flex max-h-full max-w-full items-center justify-center border border-gold/60 bg-[#efe9dd] p-3 shadow-[0_24px_60px_-30px_rgba(13,13,13,0.45)] sm:p-4"
            >
              <img
                src={img.src}
                alt={img.alt}
                loading={index === 0 ? "eager" : "lazy"}
                draggable="false"
                className="h-auto max-h-[300px] w-auto max-w-full object-contain sm:max-h-[410px] lg:max-h-[556px]"
              />
            </span>
          </motion.button>
        </AnimatePresence>
        <img src={next.src} alt="" aria-hidden="true" className="hidden" loading="lazy" />
      </div>

      <div className="mt-8 flex flex-col items-center gap-5">
        <AnimatePresence mode="wait">
          <motion.p
            key={img.caption}
            data-testid={`${testid}-caption`}
            className="font-serif text-xl font-medium italic text-[#6e5327] md:text-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: dur * 0.75 }}
          >
            {img.caption}
          </motion.p>
        </AnimatePresence>

        <div className="flex items-center gap-8">
          <button
            type="button"
            onClick={() => nav(-1)}
            aria-label={t.lightbox.prev}
            data-testid={`${testid}-prev-btn`}
            className="flex h-11 w-11 items-center justify-center border border-[#8a6f45]/60 text-midnight/80 transition-colors duration-300 hover:border-gold hover:text-[#6e5327]"
          >
            <ChevronLeft size={20} strokeWidth={1.25} />
          </button>
          <span
            data-testid={`${testid}-counter`}
            className="hv-nums font-sans text-[11px] font-medium tracking-[0.4em] text-[#6e5327]"
          >
            {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </span>
          <button
            type="button"
            onClick={() => nav(1)}
            aria-label={t.lightbox.next}
            data-testid={`${testid}-next-btn`}
            className="flex h-11 w-11 items-center justify-center border border-[#8a6f45]/60 text-midnight/80 transition-colors duration-300 hover:border-gold hover:text-[#6e5327]"
          >
            <ChevronRight size={20} strokeWidth={1.25} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GalleryFrame;
